import { IconImageM } from "@common/components/icons/IconsM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import React, { useState } from "react"
import { Keyboard, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native"
import { Searchbar } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import {
    ACTIVITY_PERIOD_FILTERS,
    ACTIVITY_TYPE_FILTERS,
    EMPTY_ACTIVITY_SEARCH,
    isActivitySearchActive,
    toggleActivitySearch,
} from "../../../ports/primary/ui/formatters/activitySearchFormatter"

const FilterPill = ({ label, icon, iconUri, selected, onPress }) => (
    <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.7}
        style={[styles.pill, selected && styles.pillSelected]}
    >
        {iconUri ? (
            <IconImageM uri={iconUri} width={16} height={16} />
        ) : (
            <FontAwesome5
                name={icon}
                size={13}
                color={selected ? theme.colors.primary : theme.colors.grey}
            />
        )}
        <TextM
            fontSize={13}
            fontWeight={selected ? "SemiBold" : "Medium"}
            color={selected ? theme.colors.primary : theme.colors.grey}
        >
            {label}
        </TextM>
    </TouchableOpacity>
)

const FilterGroup = ({ title, children }) => (
    <View style={styles.group}>
        <TextM fontSize={11} fontWeight="SemiBold" color={theme.colors.grey}>
            {title}
        </TextM>
        <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            // Clavier levé, le premier toucher choisit le filtre au lieu de seulement baisser le clavier.
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={styles.row}
        >
            {children}
        </ScrollView>
    </View>
)

/**
 * La recherche des « Activités faites », comme celle de WhatsApp : au repos la barre seule ; au
 * toucher, les filtres s'ouvrent dessous, chacun avec son icône (le type, la matière et son
 * picto, la période) ; refermée, un filtre posé reste en puce sous la barre — la toucher le
 * retire. Les mêmes libellés, icônes et règle que l'espace parent (`activitySearchFormatter`).
 */
const RealizationsSearchBar = ({ search, onChange, subjects, resultCount, width }) => {
    const [open, setOpen] = useState(false)
    // Refermer, c'est aussi baisser le clavier : le champ perd le focus, et le toucher suivant sur
    // la barre rouvre les filtres.
    const close = () => {
        setOpen(false)
        Keyboard.dismiss()
    }

    const toggle = (field, key) => onChange(toggleActivitySearch(search, field, key))

    const type = ACTIVITY_TYPE_FILTERS.find((f) => f.key === search.type)
    const subject = subjects.find((s) => String(s.id) === String(search.subjectId))
    const period = ACTIVITY_PERIOD_FILTERS.find((f) => f.key === search.period)

    return (
        <View style={[styles.container, { width }]}>
            <View style={styles.barRow}>
                <Searchbar
                    placeholder="Rechercher une activité"
                    value={search.query}
                    onChangeText={(query) => onChange({ ...search, query })}
                    onFocus={() => setOpen(true)}
                    onSubmitEditing={close}
                    // La borne du serveur : au-delà de 100 caractères, il refuse la recherche.
                    maxLength={100}
                    style={styles.searchbar}
                    inputStyle={{ fontSize: 14, minHeight: 0 }}
                    iconColor={theme.colors.grey}
                />
                {open && (
                    <TouchableOpacity onPress={close} style={styles.ok}>
                        <TextM fontWeight="SemiBold" color={theme.colors.primary}>
                            OK
                        </TextM>
                    </TouchableOpacity>
                )}
            </View>

            {open ? (
                <View style={styles.panel}>
                    <FilterGroup title="Type">
                        {ACTIVITY_TYPE_FILTERS.map((f) => (
                            <FilterPill
                                key={f.key}
                                label={f.label}
                                icon={f.icon}
                                selected={search.type === f.key}
                                onPress={() => toggle("type", f.key)}
                            />
                        ))}
                    </FilterGroup>
                    {subjects.length > 0 && (
                        <FilterGroup title="Matière">
                            {subjects.map((s) => (
                                <FilterPill
                                    key={s.id}
                                    label={s.name}
                                    iconUri={s.icon}
                                    selected={String(search.subjectId) === String(s.id)}
                                    onPress={() => toggle("subjectId", s.id)}
                                />
                            ))}
                        </FilterGroup>
                    )}
                    <FilterGroup title="Période">
                        {ACTIVITY_PERIOD_FILTERS.map((f) => (
                            <FilterPill
                                key={f.key}
                                label={f.label}
                                icon={f.icon}
                                selected={search.period === f.key}
                                onPress={() => toggle("period", f.key)}
                            />
                        ))}
                    </FilterGroup>
                </View>
            ) : (
                (type || subject || period) && (
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled"
                        contentContainerStyle={styles.row}
                    >
                        {type && (
                            <FilterPill
                                label={type.label}
                                icon={type.icon}
                                selected
                                onPress={() => toggle("type", type.key)}
                            />
                        )}
                        {subject && (
                            <FilterPill
                                label={subject.name}
                                iconUri={subject.icon}
                                selected
                                onPress={() => toggle("subjectId", subject.id)}
                            />
                        )}
                        {period && (
                            <FilterPill
                                label={period.label}
                                icon={period.icon}
                                selected
                                onPress={() => toggle("period", period.key)}
                            />
                        )}
                    </ScrollView>
                )
            )}

            {isActivitySearchActive(search) && (
                <View style={styles.summary}>
                    <TextM fontSize={12} color={theme.colors.grey}>
                        {resultCount === 1 ? "1 activité" : `${resultCount ?? 0} activités`}
                    </TextM>
                    <TouchableOpacity onPress={() => onChange(EMPTY_ACTIVITY_SEARCH)}>
                        <TextM fontSize={12} fontWeight="SemiBold" color={theme.colors.primary}>
                            Tout effacer
                        </TextM>
                    </TouchableOpacity>
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        alignSelf: "center",
        paddingHorizontal: 10,
        paddingTop: 10,
        paddingBottom: 8,
        gap: 10,
        backgroundColor: theme.colors.white,
        borderBottomWidth: 1,
        borderColor: theme.colors.lightMore,
    },
    barRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    searchbar: {
        flex: 1,
        height: 44,
        borderRadius: 12,
        backgroundColor: theme.colors.white,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        elevation: 0,
        shadowOpacity: 0,
    },
    ok: {
        paddingHorizontal: 6,
        paddingVertical: 8,
    },
    panel: {
        gap: 10,
    },
    group: {
        gap: 6,
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    pill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 7,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    pillSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    summary: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingHorizontal: 4,
    },
})

export default RealizationsSearchBar
